import { useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Stack, Tooltip, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import type { SuperAdminInvitation } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'

/// Super-admin cross-org view of stuck user invitations. The org admin
/// can already resend + revoke their own invitations from PeoplePage,
/// but if email delivery dies before the org admin even knows it, the
/// row stays "pending" forever and `createInvitation` refuses to let
/// them re-invite the same email. Deleting from here clears the block.
export function InvitationsTab() {
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: ['super-admin', 'invitations'],
    queryFn: () => api.superAdmin.listAllInvitations(),
  })

  const items = list.data ?? []

  const [confirmFor, setConfirmFor] = useState<SuperAdminInvitation | null>(null)

  const revoke = useMutation({
    mutationFn: (row: SuperAdminInvitation) =>
      api.superAdmin.revokeInvitationInOrg(row.org_slug, row.id),
    onSuccess: () => {
      toast.success('Invitation revoked — the workspace admin can re-invite this email')
      setConfirmFor(null)
      qc.invalidateQueries({ queryKey: ['super-admin', 'invitations'] })
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Pending user invitations
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Every workspace's pending invite emails. Use this to clear stuck
            invites when email delivery failed — once revoked, the workspace
            admin can re-invite the same email.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <Button
            onClick={() => list.refetch()}
            startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
            variant="text"
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Tooltip>
      </Stack>

      {list.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Box sx={{
            display: 'inline-flex', width: 56, height: 56,
            borderRadius: 99, bgcolor: 'action.hover',
            alignItems: 'center', justifyContent: 'center', mb: 2,
          }}>
            <MailOutlineIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            No pending invitations anywhere.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
            When a workspace admin invites someone and email delivery fails,
            it shows up here.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {items.map((inv) => (
            <Row
              key={`${inv.org_slug}:${inv.id}`}
              inv={inv}
              onRevoke={() => setConfirmFor(inv)}
              busy={revoke.isPending && revoke.variables?.id === inv.id}
            />
          ))}
        </Stack>
      )}

      <Dialog open={!!confirmFor} onClose={() => setConfirmFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Revoke this invitation?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5 }}>
            <strong>{confirmFor?.email}</strong> will no longer be able to use the
            existing invite link for{' '}
            <strong>{confirmFor?.org_name}</strong>. The workspace admin can
            send them a new invite from People &rarr; Add member.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmFor(null)}>Cancel</Button>
          <Button
            onClick={() => confirmFor && revoke.mutate(confirmFor)}
            disabled={revoke.isPending}
            color="error" variant="contained"
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function Row({
  inv, onRevoke, busy,
}: {
  inv: SuperAdminInvitation
  onRevoke: () => void
  busy: boolean
}) {
  const { formatDate } = useOrgSettings()
  const initials = (inv.full_name ?? inv.email).slice(0, 2).toUpperCase()
  const sentAt = formatDate(inv.created_at, true)
  // Show expiry as a chip — `< 0` means the original 72h window has
  // already lapsed, which the backend treats as still-pending until the
  // row is revoked / accepted.
  const expiresMs = new Date(inv.expires_at).getTime() - Date.now()
  const expired = expiresMs <= 0
  const hoursLeft = Math.floor(expiresMs / 3_600_000)

  return (
    <Box sx={{
      p: 2, borderRadius: 2, border: '1px solid',
      borderColor: 'divider', bgcolor: 'background.paper',
      display: 'flex', alignItems: 'center', gap: 2,
    }}>
      <Avatar sx={{
        bgcolor: 'primary.light', color: 'primary.main',
        fontSize: 13, fontWeight: 700, width: 38, height: 38,
      }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography sx={{
            fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em',
          }} noWrap>
            {inv.full_name ?? inv.email}
          </Typography>
          <Chip size="small" label={inv.org_name} variant="outlined"
            sx={{ fontSize: 11, fontWeight: 600, height: 20 }} />
          {inv.role_name && (
            <Chip size="small" label={inv.role_name}
              sx={{ fontSize: 11, fontWeight: 600, height: 20,
                bgcolor: 'action.hover', color: 'text.secondary' }} />
          )}
          {expired ? (
            <Chip size="small" color="warning" variant="outlined"
              label="Link expired" sx={{ fontSize: 11, fontWeight: 700, height: 20 }} />
          ) : (
            <Chip size="small" variant="outlined"
              label={hoursLeft >= 1 ? `${hoursLeft}h left` : '<1h left'}
              sx={{ fontSize: 11, fontWeight: 600, height: 20 }} />
          )}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
          {inv.full_name ? `${inv.email} · ` : ''}sent {sentAt}
          {inv.invited_by_name ? ` by ${inv.invited_by_name}` : ''}
        </Typography>
      </Box>

      <Tooltip title="Revoke this invitation so the workspace admin can re-invite">
        <span>
          <Button
            onClick={onRevoke}
            disabled={busy}
            startIcon={busy
              ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
              : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            variant="outlined" color="error"
            sx={{
              textTransform: 'none', borderRadius: 999, px: 2.5,
              whiteSpace: 'nowrap',
            }}
          >
            {busy ? 'Revoking…' : 'Revoke'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  )
}
